// src/react/context.ts
import {
  createContext,
  useMemo,
  useContext,
  useState,
  createElement
} from "react";

// src/api.ts
import { ok } from "oazapfts";

// src/flagr.gen.ts
import * as Oazapfts from "oazapfts/lib/runtime";
import * as QS from "oazapfts/lib/runtime/query";
var defaults = {
  baseUrl: "/"
};
var oazapfts = Oazapfts.runtime(defaults);
function postEvaluation(evalContext, opts) {
  return oazapfts.fetchJson("/api/v1/evaluation", oazapfts.json({
    ...opts,
    method: "POST",
    body: evalContext
  }));
}

// src/api.ts
var Cache = /* @__PURE__ */ new Map();
var deduplication = (ttl, fn) => (...args) => {
  const args_ = JSON.stringify(args);
  if (Cache.has(args_)) {
    const cache = Cache.get(args_);
    if (cache && cache.timestamp > Date.now()) {
      return cache.result;
    }
    Cache.delete(args_);
  }
  const result = fn(...args);
  Cache.set(args_, { result, timestamp: Date.now() + ttl });
  return result;
};
var evaluation = (...args) => ok(postEvaluation(...args));
var evaluationDedup = (ttl) => deduplication(ttl, evaluation);

// src/react/context.ts
var has = Function.call.bind(Object.hasOwnProperty);
var eqID = (a, b) => {
  const unified = (i) => typeof i === "object" && has(i, "flagID") ? i.flagID : i;
  return unified(a) === unified(b);
};
function createContextWithSelector(initialValue = null) {
  const context = createContext(initialValue);
  const { Provider } = context;
  const useSelector2 = function(f) {
    const _context = useContext(context);
    if (_context === null) {
      throw new Error("Provider not configure");
    } else {
      const value = f(_context);
      return useMemo(() => value, [value]);
    }
  };
  return {
    Provider,
    useSelector: useSelector2
  };
}
var loaded = (id, data) => ({
  type: "loaded",
  data,
  id
});
var error = (id, retry, error2) => ({
  type: "error",
  error: error2,
  id,
  retry
});
var isOnlyEvalContext = (id) => id.flagID !== void 0;
var M = createContextWithSelector(null);
var useSelector = M.useSelector;
var findLoadedData = (flagItems, id) => {
  var _a;
  return ((_a = flagItems.filter((flag2) => flag2.type === "loaded").find((flag2) => eqID(flag2.id, id))) == null ? void 0 : _a.data) || null;
};
var useData = (id) => useSelector(({ flagrIds }) => findLoadedData(flagrIds, id));
var update = (f, xs, x) => xs.filter(f).concat([x]);
var FlagProvider = ({
  children,
  flagUrl,
  dedupe = 3 * 1e3
}) => {
  const [state, setState] = useState({
    flagrIds: [],
    flag: flag2,
    retry: () => {
    }
  });
  function flag2(id) {
    const evaluation2 = evaluationDedup(dedupe);
    const hasListed = state.flagrIds.find((f) => f.id === id) || {
      type: "idle",
      id
    };
    if (hasListed.type === "idle") {
      evaluation2(
        isOnlyEvalContext(hasListed.id) ? hasListed.id : { flagID: hasListed.id },
        { baseUrl: flagUrl }
      ).then(
        (data) => setState((old) => ({
          ...old,
          flagrIds: update((f) => f.id !== id, old.flagrIds, loaded(id, data))
        }))
      ).catch(
        (e) => setState((old) => ({
          ...old,
          flagrIds: update((f) => f.id !== id, old.flagrIds, error(hasListed.id, 0, e))
        }))
      );
    }
    setState((prev) => ({
      ...prev,
      flagrIds: update((f) => f.id !== id, prev.flagrIds, hasListed)
    }));
  }
  const retry = (flagItem) => {
    const errorFlag = {
      ...flagItem,
      retry: flagItem.retry ? flagItem.retry + 1 : 1
    };
    const filteredFlag = state.flagrIds.filter((f) => f.id !== errorFlag.id);
    setState((prev) => ({
      ...prev,
      flagrIds: filteredFlag.concat(errorFlag)
    }));
  };
  return createElement(
    M.Provider,
    {
      value: {
        flagrIds: state.flagrIds,
        flag: flag2,
        retry
      }
    },
    children
  );
};

// src/react/flag.tsx
import { useEffect } from "react";
import { Fragment, jsx } from "react/jsx-runtime";
var useFlag = (id, predicate) => {
  const { flag: flag2, flagrIds } = useSelector(({ flag: flag3, flagrIds: flagrIds2 }) => ({ flag: flag3, flagrIds: flagrIds2 }));
  const flagged = flagrIds.find((f) => eqID(f.id, id));
  const _predicate = predicate ? predicate : (data) => data.variantKey === "on";
  useEffect(() => {
    flag2(id);
  }, []);
  return (flagged == null ? void 0 : flagged.type) === "loaded" && _predicate(flagged.data);
};
var Flag = ({
  id,
  fallback = null,
  children,
  predicate
}) => {
  const flag2 = useFlag(id, predicate);
  if (flag2) {
    return children;
  }
  return fallback ? fallback : null;
};
function flag(id, valueOrPredicate, valueOrDefault, defaultValue) {
  if (typeof id === "boolean" && typeof defaultValue === "undefined" && typeof valueOrDefault === "undefined") {
    return id ? valueOrPredicate : typeof valueOrPredicate === "string" ? "" : typeof valueOrPredicate === "number" ? 0 : typeof valueOrPredicate === "boolean" ? false : typeof valueOrPredicate === "function" ? () => {
    } : Array.isArray(valueOrPredicate) ? [] : null;
  }
  if (typeof id === "boolean" && typeof defaultValue === "undefined" && typeof valueOrDefault !== "undefined") {
    return id ? valueOrPredicate : valueOrDefault;
  }
  const typeofID = typeof id;
  const evalContext = ["string", "number"].includes(typeofID) ? { flagID: typeofID === "number" ? id : Number(id) | 0 } : id;
  if (typeof valueOrPredicate === "function" && typeof valueOrDefault !== "undefined" && typeof defaultValue !== "undefined") {
    return /* @__PURE__ */ jsx(Flag, { id: evalContext, fallback: defaultValue, predicate: valueOrPredicate, children: /* @__PURE__ */ jsx(Fragment, { children: valueOrDefault }) });
  }
  if (typeof valueOrPredicate === "function" && typeof valueOrDefault !== "undefined" && typeof defaultValue === "undefined") {
    return /* @__PURE__ */ jsx(Flag, { id: evalContext, predicate: valueOrPredicate, children: valueOrDefault });
  }
  if (valueOrPredicate !== "function" && typeof valueOrDefault !== "undefined" && typeof defaultValue === "undefined") {
    return /* @__PURE__ */ jsx(Flag, { id: evalContext, fallback: valueOrDefault, children: valueOrPredicate });
  }
  return /* @__PURE__ */ jsx(Flag, { id: evalContext, children: valueOrPredicate });
}
export {
  Flag,
  FlagProvider,
  eqID,
  flag,
  useData,
  useFlag,
  useSelector
};
